/**
 * @file DeleteOrderModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-10
 * @modified 2026-06-10
 * @description Confirm dialog for deleting a customer order that has linked POs.
 *              Lists each linked purchase order with a tick (all ticked by
 *              default) so the user chooses which POs to delete alongside the
 *              order; unticked POs are kept but unlinked. Only DRAFT/local POs
 *              can be deleted, so non-DRAFT POs render disabled (kept).
 */

import { useState } from 'react';
import Modal from './Modal';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import './DeleteOrderModal.css';

const isDeletablePO = (po) => {
  const xid = po.xero_purchase_order_id || '';
  const isLocal = xid.startsWith('TEMP-') || xid.startsWith('LOCAL-');
  return isLocal || po.status === 'DRAFT' || po.status === 'Draft';
};

// Body extracted so useSlideOutButton fires after the Modal portal mounts (see
// the modal slide-out note in CLAUDE.md).
function DeleteOrderBody({ order, linkedPOs, checked, toggle, loading }) {
  useSlideOutButton([loading, checked]);

  return (
    <>
      <p className="delete-order-intro">
        Deleting order <strong>{order.order_number}</strong> can't be undone. Choose which linked
        purchase orders to delete with it — unticked POs are kept but unlinked from this order.
      </p>
      <div className="delete-order-po-list">
        {linkedPOs.map(po => {
          const deletable = isDeletablePO(po);
          return (
            <div key={po.id} className={`delete-order-po-row${deletable ? '' : ' not-deletable'}`}>
              <TickCheckbox
                id={`del-po-${po.id}`}
                checked={checked.has(po.id)}
                disabled={!deletable}
                onChange={() => deletable && toggle(po.id)}
                ariaLabel={`Delete PO ${po.purchase_order_number}`}
              />
              <span className="po-number">{po.purchase_order_number}</span>
              <span className="po-supplier" title={po.supplier_name || ''}>{po.supplier_name || '—'}</span>
              <span className={`status-badge ${String(po.status || '').toLowerCase()}`}>{po.status}</span>
              <span className="po-total">{formatCurrency(po.total)}</span>
              {!deletable && (
                <span className="po-kept-note" title="Only DRAFT POs can be deleted — this one will be kept and unlinked">
                  kept
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function DeleteOrderModal({ order, linkedPOs, loading, onConfirm, onCancel }) {
  const [checked, setChecked] = useState(
    () => new Set(linkedPOs.filter(isDeletablePO).map(p => p.id))
  );

  const toggle = (id) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const deleteCount = checked.size;

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={`Delete Order ${order.order_number}`}
      size="md"
      className="delete-order"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onCancel} disabled={loading}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout btn-danger"
              onClick={() => onConfirm(Array.from(checked))}
              disabled={loading}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">delete</span></div>
              <span className="btn-label">
                {loading
                  ? 'Deleting...'
                  : `Delete Order${deleteCount > 0 ? ` + ${deleteCount} PO${deleteCount !== 1 ? 's' : ''}` : ''}`}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <DeleteOrderBody
        order={order}
        linkedPOs={linkedPOs}
        checked={checked}
        toggle={toggle}
        loading={loading}
      />
    </Modal>
  );
}

export default DeleteOrderModal;
